import { useEffect, useRef } from 'react';
import type { RoughCanvas } from 'roughjs/bin/canvas';

/**
 * Shared hook for Rough.js canvas components.
 * Handles: DPR scaling, dynamic roughjs import, ResizeObserver with
 * debounced redraw, and cleanup on unmount.
 *
 * @param draw - Callback receiving (rc, width, height) to draw with.
 *               Called on mount and on resize.
 * @param deps - Dependency array that triggers a redraw when changed.
 * @param observeTarget - 'parent' (default) observes canvas.parentElement,
 *                        'canvas' observes the canvas itself.
 */
export function useRoughCanvas(
  draw: (rc: RoughCanvas, w: number, h: number) => void,
  deps: unknown[],
  observeTarget: 'parent' | 'canvas' = 'parent',
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const target = observeTarget === 'parent' ? canvas.parentElement : canvas;
    if (!target) return;

    let cancelled = false;

    const render = async () => {
      const rough = (await import('roughjs')).default;
      if (cancelled) return;

      const dpr = window.devicePixelRatio || 1;
      const w = target.clientWidth;
      const h = target.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      const rc = rough.canvas(canvas);
      draw(rc, w, h);
    };

    render();

    const ro = new ResizeObserver(() => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(render, 300);
    });
    ro.observe(target);

    return () => {
      cancelled = true;
      ro.disconnect();
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return canvasRef;
}
